import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

const Placeorder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getCustomerId = () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.id || parsed.customer_id || null;
      }
    } catch (e) {}
    return null;
  };

  const getImageUrl = (item) => {
    // For combo items
    if (item.item_type === 'combo' && item.combo_image) {
      const imageUrl = item.combo_image;
      return imageUrl.startsWith('http') ? imageUrl : `https://evercorenutrition.com${imageUrl}`;
    }
    
    // For regular products
    const imageUrl = 
      item.product_data?.main_image ||
      item.product_data?.variant?.image ||
      item.variant_image || 
      item.product_image ||
      item.image;
    
    if (!imageUrl) return '/assets/images/Yeast_Protein.png';
    return imageUrl.startsWith('http') ? imageUrl : `https://evercorenutrition.com${imageUrl}`;
  };

  const getProductName = (item) => {
    // For combo items - use combo_name directly from backend
    if (item.item_type === 'combo') {
      return item.combo_name || item.name || 'Combo Product';
    }
    
    // For regular products
    if (item.product_data?.name) {
      return item.product_data.name;
    }
    
    if (item.product_name) {
      const cleanName = item.product_name.split(' (From Combo:')[0];
      return cleanName;
    }
    
    return 'Unknown Product';
  };

  const getFlavour = (item) => {
    // Combos don't have flavour
    if (item.item_type === 'combo') return null;
    
    if (item.product_data?.variant?.flavour) {
      const flavour = item.product_data.variant.flavour;
      if (flavour !== 'Default' && flavour !== 'default') {
        return flavour;
      }
    }
    
    if (item.product_data?.flavour) {
      const flavour = item.product_data.flavour;
      if (flavour !== 'Default' && flavour !== 'default') {
        return flavour;
      }
    }
    
    if (item.variant_details?.flavour) {
      const flavour = item.variant_details.flavour;
      if (flavour !== 'Default' && flavour !== 'default') {
        return flavour;
      }
    }
    
    return null;
  };

  const getWeight = (item) => {
    // Combos don't have weight
    if (item.item_type === 'combo') return null;
    
    if (item.product_data?.variant) {
      const variant = item.product_data.variant;
      if (variant.weight_value && variant.weight_unit) {
        return `${variant.weight_value}${variant.weight_unit}`;
      }
      if (variant.weight_display) {
        return variant.weight_display;
      }
    }
    
    if (item.variant_details?.weight) {
      return item.variant_details.weight;
    }
    
    return null;
  };

  const getVariantSku = (item) => {
    if (item.item_type === 'combo') return 'COMBO';
    
    if (item.product_data?.variant?.sku) {
      return item.product_data.variant.sku;
    }
    if (item.variant_details?.sku) {
      return item.variant_details.sku;
    }
    if (item.product_data?.sku) {
      return item.product_data.sku;
    }
    return 'N/A';
  };

  const getMrp = (item) => {
    if (item.item_type === 'combo') return null;
    
    if (item.product_data?.variant?.mrp) {
      return item.product_data.variant.mrp;
    }
    if (item.variant_details?.mrp) {
      return item.variant_details.mrp;
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return '#f59e0b';
      case 'confirmed': return '#3b82f6';
      case 'shipped': return '#8b5cf6';
      case 'delivered': return '#10b981';
      case 'cancelled':
      case 'cancel requested': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const handleTrackOrder = (orderId) => {
    navigate(`/track-order/${orderId}`);
  };

  const fetchOrderById = async (id) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please login to view your orders');
        setLoading(false);
        return;
      }

      // console.log('Fetching order:', id);
      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/customer/orders/${id}/`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Order not found');
        }
        throw new Error('Failed to fetch order');
      }

      const data = await response.json();
      // console.log('Order data received:', data);
      
      if (data.success && data.order) {
        if (data.order.items && data.order.items[0]) {
          // console.log('First item structure:', data.order.items[0]);
          // console.log('Item type:', data.order.items[0].item_type);
          // console.log('Combo name:', data.order.items[0].combo_name);
        }
        setOrder(data.order);
      } else {
        throw new Error('Invalid order data');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestOrder = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const customerId = getCustomerId();

      if (!token || !customerId) {
        setError('Please login to view your orders');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/orders/?customer_id=${customerId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch orders');

      const data = await response.json();
      const list = data.results || data || [];
      
      if (list.length > 0) {
        await fetchOrderById(list[0].order_id);
      } else {
        setError('No orders found');
        setLoading(false);
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    const pendingOrderId = sessionStorage.getItem('pendingOrderId');
    if (pendingOrderId) {
      sessionStorage.removeItem('pendingOrderId');
      fetchOrderById(pendingOrderId);
    } else if (orderId) {
      fetchOrderById(orderId);
    } else {
      fetchLatestOrder();
    }
  }, [orderId]);

  if (loading) {
    return (
      <>
        <Navbar />
        <section className="cart-section section-b-space">
          <div className="container-fluid-lg" style={{ textAlign: 'center', padding: '60px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: '10px', color: '#666' }}>Loading your order...</p>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <section className="cart-section section-b-space">
          <div className="container-fluid-lg" style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666' }}>{error}</p>
            <a href="/" className="btn btn-dark mt-3">Go Home</a>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  if (!order) {
    return (
      <>
        <Navbar />
        <section className="cart-section section-b-space">
          <div className="container-fluid-lg" style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666' }}>No order found</p>
            <a href="/" className="btn btn-dark mt-3">Go Home</a>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain breadcrumb-order" style={{ display: "flex", justifyContent: "center" }}>
                <div className="order-box">
                  <div className="order-image">
                    <div className="checkmark">
                      {[...Array(6)].map((_, i) => (
                        <svg key={i} className="star" height="19" viewBox="0 0 19 19" width="19" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8.296.747c.532-.972 1.393-.973 1.925 0l2.665 4.872 4.876 2.66c.974.532.975 1.393 0 1.926l-4.875 2.666-2.664 4.876c-.53.972-1.39.973-1.924 0l-2.664-4.876L.76 10.206c-.972-.532-.973-1.393 0-1.925l4.872-2.66L8.296.746z"></path>
                        </svg>
                      ))}
                      <svg className="checkmark__check" height="36" viewBox="0 0 48 36" width="48" xmlns="http://www.w3.org/2000/svg">
                        <path d="M47.248 3.9L43.906.667a2.428 2.428 0 0 0-3.344 0l-23.63 23.09-9.554-9.338a2.432 2.432 0 0 0-3.345 0L.692 17.654a2.236 2.236 0 0 0 .002 3.233l14.567 14.175c.926.894 2.42.894 3.342.01L47.248 7.128c.922-.89.922-2.34 0-3.23"></path>
                      </svg>
                      <svg className="checkmark__background" height="115" viewBox="0 0 120 115" width="120" xmlns="http://www.w3.org/2000/svg">
                        <path d="M107.332 72.938c-1.798 5.557 4.564 15.334 1.21 19.96-3.387 4.674-14.646 1.605-19.298 5.003-4.61 3.368-5.163 15.074-10.695 16.878-5.344 1.743-12.628-7.35-18.545-7.35-5.922 0-13.206 9.088-18.543 7.345-5.538-1.804-6.09-13.515-10.696-16.877-4.657-3.398-15.91-.334-19.297-5.002-3.356-4.627 3.006-14.404 1.208-19.962C10.93 67.576 0 63.442 0 57.5c0-5.943 10.93-10.076 12.668-15.438 1.798-5.557-4.564-15.334-1.21-19.96 3.387-4.674 14.646-1.605 19.298-5.003C35.366 13.73 35.92 2.025 41.45.22c5.344-1.743 12.628 7.35 18.545 7.35 5.922 0 13.206-9.088 18.543-7.345 5.538 1.804 6.09 13.515 10.696 16.877 4.657 3.398 15.91.334 19.297 5.002 3.356 4.627-3.006 14.404-1.208 19.962C109.07 47.424 120 51.562 120 57.5c0 5.943-10.93 10.076-12.668 15.438z"></path>
                      </svg>
                    </div>
                  </div>

                  <div className="order-contain">
                    <h3 className="theme-color">Order Placed</h3>
                    
                    <h6>Order ID: <strong>{order.order_id}</strong></h6>
                    <button 
                      className="btn btn-primary mt-3"
                      onClick={() => handleTrackOrder(order.order_id)}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '25px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      <i className="fa-solid fa-truck me-2"></i>
                      Track Your Order
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cart-section section-b-space">
        <div className="container-fluid-lg">
          <div className="row g-sm-4 g-3">

            <div className="col-xxl-9 col-lg-8">
              <div className="cart-table order-table order-table-2">
                {/* Desktop View */}
                <div className="d-none d-md-block">
                  <div className="table-responsive">
                    <table className="table mb-0" style={{ minWidth: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '45%' }}>Product</th>
                          <th style={{ width: '15%' }}>Unit Price</th>
                          <th style={{ width: '15%' }}>Quantity</th>
                          <th style={{ width: '25%' }}>Total Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item, index) => {
                          const itemTotal = (parseFloat(item.price || item.unit_price) * item.quantity).toFixed(2);
                          const productName = getProductName(item);
                          const flavour = getFlavour(item);
                          const weight = getWeight(item);
                          const variantSku = getVariantSku(item);
                          const mrp = getMrp(item);
                          const isComboItem = item.item_type === 'combo';
                          const comboName = item.combo_name;
                          const imageUrl = getImageUrl(item);
                          
                          return (
                            <tr key={item.id || index}>
                              <td className="product-detail">
                                <div className="product border-0 d-flex align-items-start gap-3">
                                  <a href={isComboItem ? `/combo/${item.combo_id}` : `/product/${item.product_data?.id || item.product}`} className="product-image" style={{ width: '70px', flexShrink: 0 }}>
                                    <img
                                      src={imageUrl}
                                      className="img-fluid blur-up lazyload"
                                      alt={productName}
                                      style={{ width: '100%', objectFit: 'contain' }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/assets/images/Yeast_Protein.png';
                                      }}
                                    />
                                  </a>
                                  <div className="product-detail flex-grow-1">
                                    <ul style={{ paddingLeft: 0, marginBottom: 0, listStyle: 'none' }}>
                                      <li className="name mb-1">
                                        <a href={isComboItem ? `/combo/${item.combo_id}` : `/product/${item.product_data?.id || item.product}`} className="fw-semibold text-dark">
                                          {productName}
                                        </a>
                                      </li>
                                      
                                      {isComboItem && comboName && (
                                        <li className="text-content mb-1">
                                          <span style={{ 
                                            background: '#e8f0fe', 
                                            padding: '2px 8px', 
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            color: '#0d6efd'
                                          }}>
                                            <i className="fa-solid fa-layer-group" style={{ fontSize: '10px' }}></i>
                                            {comboName.length > 50 ? comboName.substring(0, 50) + '...' : comboName}
                                          </span>
                                        </li>
                                      )}
                                      
                                      {flavour && (
                                        <li className="text-content mb-1">
                                          <span style={{ 
                                            background: '#f3f4f6', 
                                            padding: '2px 8px', 
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }}>
                                            <i className="fa-solid fa-flask" style={{ fontSize: '10px' }}></i>
                                            {flavour}
                                          </span>
                                        </li>
                                      )}
                                      
                                      {weight && (
                                        <li className="text-content mb-1">
                                          <span style={{ 
                                            background: '#f3f4f6', 
                                            padding: '2px 8px', 
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }}>
                                            <i className="fa-solid fa-weight-scale" style={{ fontSize: '10px' }}></i>
                                            {weight}
                                          </span>
                                        </li>
                                      )}
                                      
                                      {(flavour || weight) && variantSku !== 'N/A' && variantSku !== 'COMBO' && (
                                        <li className="text-muted" style={{ fontSize: '10px', marginTop: '4px' }}>
                                          SKU: {variantSku}
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              </td>

                              <td className="price align-middle">
                                <h6 className="mb-0 theme-color">₹{parseFloat(item.price || item.unit_price).toFixed(2)}</h6>
                                {mrp && mrp > parseFloat(item.price) && (
                                  <small style={{ textDecoration: 'line-through', color: '#999', fontSize: '10px', display: 'block' }}>
                                    MRP: ₹{mrp.toFixed(2)}
                                  </small>
                                )}
                              </td>

                              <td className="quantity align-middle">
                                <h5 className="mb-0">{String(item.quantity).padStart(2, '0')}</h5>
                              </td>

                              <td className="subtotal align-middle">
                                <h5 className="mb-0 fw-bold">₹{itemTotal}</h5>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile View */}
                <div className="d-md-none">
                  {order.items.map((item, index) => {
                    const itemTotal = (parseFloat(item.price || item.unit_price) * item.quantity).toFixed(2);
                    const productName = getProductName(item);
                    const flavour = getFlavour(item);
                    const weight = getWeight(item);
                    const variantSku = getVariantSku(item);
                    const mrp = getMrp(item);
                    const isComboItem = item.item_type === 'combo';
                    const comboName = item.combo_name;
                    const imageUrl = getImageUrl(item);
                    
                    return (
                      <div key={item.id || index} className="order-card-mobile mb-3" style={{ 
                        background: '#fff',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                      }}>
                        <div className="p-3">
                          <div className="d-flex gap-3" style={{ flexWrap: 'wrap' }}>
                            {/* Product Image */}
                            <div style={{ width: '80px', flexShrink: 0 }}>
                              <img
                                src={imageUrl}
                                className="img-fluid rounded"
                                alt={productName}
                                style={{ width: '100%', objectFit: 'contain', maxHeight: '80px' }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/assets/images/Yeast_Protein.png';
                                }}
                              />
                            </div>
                            
                            {/* Product Details */}
                            <div style={{ flex: '1', minWidth: '180px' }}>
                              <h6 className="mb-1 fw-bold" style={{ fontSize: '14px' }}>
                                <a href={isComboItem ? `/combo/${item.combo_id}` : `/product/${item.product_data?.id || item.product}`} className="text-dark text-decoration-none">
                                  {productName}
                                </a>
                              </h6>
                              
                              {isComboItem && comboName && (
                                <div className="mb-2">
                                  <span style={{ 
                                    background: '#e8f0fe', 
                                    padding: '2px 8px', 
                                    borderRadius: '12px',
                                    fontSize: '10px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: '#0d6efd'
                                  }}>
                                    <i className="fa-solid fa-layer-group" style={{ fontSize: '9px' }}></i>
                                    {comboName.length > 35 ? comboName.substring(0, 35) + '...' : comboName}
                                  </span>
                                </div>
                              )}
                              
                              <div className="d-flex flex-wrap gap-2 mb-2">
                                {flavour && (
                                  <span className="badge bg-light text-dark" style={{ fontSize: '11px', fontWeight: '500', padding: '4px 8px' }}>
                                    <i className="fa-solid fa-flask me-1"></i> {flavour}
                                  </span>
                                )}
                                {weight && (
                                  <span className="badge bg-light text-dark" style={{ fontSize: '11px', fontWeight: '500', padding: '4px 8px' }}>
                                    <i className="fa-solid fa-weight-scale me-1"></i> {weight}
                                  </span>
                                )}
                              </div>
                              
                              {(flavour || weight) && variantSku !== 'N/A' && variantSku !== 'COMBO' && (
                                <small className="text-muted d-block mb-2" style={{ fontSize: '10px' }}>
                                  SKU: {variantSku}
                                </small>
                              )}
                            </div>
                          </div>
                          
                          {/* Price and Quantity Row */}
                          <div className="mt-3 pt-2 border-top d-flex justify-content-between align-items-center">
                            <div>
                              <span className="text-muted" style={{ fontSize: '12px' }}>Price:</span>
                              <span className="fw-bold ms-1" style={{ fontSize: '14px' }}>₹{parseFloat(item.price || item.unit_price).toFixed(2)}</span>
                              {mrp && mrp > parseFloat(item.price) && (
                                <small className="text-muted ms-1" style={{ textDecoration: 'line-through', fontSize: '11px' }}>
                                  ₹{mrp.toFixed(2)}
                                </small>
                              )}
                            </div>
                            <div>
                              <span className="text-muted" style={{ fontSize: '12px' }}>Qty:</span>
                              <span className="fw-bold ms-1" style={{ fontSize: '14px' }}>{item.quantity}</span>
                            </div>
                            <div className="text-end">
                              <span className="text-muted d-block" style={{ fontSize: '11px' }}>Total</span>
                              <span className="fw-bold text-primary" style={{ fontSize: '16px' }}>₹{itemTotal}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="col-xxl-3 col-lg-4">
              <div className="row g-4">

                <div className="col-lg-12 col-sm-6">
                  <div className="summery-box">
                    <div className="summery-header">
                      <h3>Price Details</h3>
                      <h5 className="ms-auto theme-color">({order.total_items || order.items.length} Items)</h5>
                    </div>
                    <ul className="summery-contain">
                      <li>
                        <h4>Subtotal</h4>
                        <h4 className="price">₹{parseFloat(order.subtotal).toFixed(2)}</h4>
                      </li>
                      {parseFloat(order.discount_amount) > 0 && (
                        <li>
                          <h4>Discount</h4>
                          <h4 className="price theme-color">- ₹{parseFloat(order.discount_amount).toFixed(2)}</h4>
                        </li>
                      )}
                      <li>
                        <h4>Shipping</h4>
                        <h4 className="price">₹{parseFloat(order.shipping_charge).toFixed(2)}</h4>
                      </li>
                    </ul>
                    <ul className="summery-total">
                      <li className="list-total">
                        <h4>Total</h4>
                        <h4 className="price">₹{parseFloat(order.total_amount).toFixed(2)}</h4>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="col-lg-12 col-sm-6">
                  <div className="summery-box">
                    <div className="summery-header d-block">
                      <h3>Shipping Address</h3>
                    </div>
                    <ul className="summery-contain pb-0 border-bottom-0">
<li className="d-block">

  {(() => {

    let address = order.pickup_address || order.shipping_address;

    // ✅ If JSON string then convert to object
    if (typeof address === "string") {
      try {
        address = JSON.parse(address);
      } catch (e) {}
    }

    // ✅ If object
    if (address && typeof address === "object") {
      return (
        <div style={{ lineHeight: "1.7" }}>

          <h4 style={{ marginBottom: "5px" }}>
            {address.receiver_name}
          </h4>

          <p style={{ marginBottom: "5px", color: "#666" }}>
            📞 {address.receiver_mobile}
          </p>

          <p style={{ marginBottom: "0", color: "#666" }}>
            {address.street},
            <br />
            {address.city}, {address.state} - {address.zip_code}
            <br />
            {address.country}
          </p>

        </div>
      );
    }

    // ✅ Normal string fallback
    return (
      <h4>
        {address || "Address not available"}
      </h4>
    );

  })()}

</li>
                      <li className="pb-0">
                        <h4>Order Status:</h4>
                        <h4 className="price" style={{ color: getStatusColor(order.status) }}>
                          {order.status_display || order.status}
                        </h4>
                      </li>
                      <li className="pb-0">
                        <h4>Order Date:</h4>
                        <h4 className="price">{order.created_at?.split(' ')[0]}</h4>
                      </li>
                    </ul>
                    <ul className="summery-total">
                      <li className="list-total border-top-0 pt-2">
                        <button 
                          onClick={() => handleTrackOrder(order.order_id)}
                          className="btn btn-link text-primary p-0"
                          style={{
                            background: 'none',
                            border: 'none',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fa-solid fa-truck me-2"></i>
                          Track Your Order
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="col-12">
                  <div className="summery-box">
                    <div className="summery-header d-block">
                      <h3>Payment Method</h3>
                    </div>
                    <ul className="summery-contain pb-0 border-bottom-0">
                      <li className="d-block pt-0">
                        <p className="text-content">
                          <strong>{order.payment_type_display || order.payment_type}</strong>
                          {order.payment_type === 'COD' && (
                            <span>  Pay on Delivery (Cash/Card). </span>
                          )}
                        </p>
                        <p className="text-muted small mt-2">
                          Payment Status: <strong>{order.payment_status_display || order.payment_status}</strong>
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Placeorder;